import { MaterialCommunityIcons } from "@expo/vector-icons";
import { useFocusEffect, useRouter } from "expo-router";
import { useCallback, useState } from "react";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { Text } from "react-native-paper";

import { useAuth } from "@/lib/auth";
import { fetchRnCompliance, type ComplianceBanner as Banner } from "@/lib/compliance-api";
import { shouldShowComplianceBanner } from "@/lib/compliance-banner-visibility";
import { colors, fonts, radius, spacing } from "@/lib/theme";

const TONE = {
    error: { bg: colors.error50, border: colors.error400, fg: colors.error600 },
    warning: { bg: colors.warning50, border: colors.warning400, fg: colors.warning600 }
} as const;

/**
 * Home-screen compliance banner. Renders nothing when the RN has no
 * compliance issues — safe to mount unconditionally. Tapping it opens the
 * My Compliance screen.
 */
export function ComplianceBanner() {
    const router = useRouter();
    const { user } = useAuth();
    const rnId = user?.rnId ? String(user.rnId) : "";
    const show = shouldShowComplianceBanner(user?.rnStatus);
    const [banner, setBanner] = useState<Banner | null>(null);

    useFocusEffect(
        useCallback(() => {
            let cancelled = false;
            //  Don't even ask the server while she is still an applicant —
            //  there is nothing to render and no reason to spend the request.
            if (!rnId || !show) {
                return;
            }
            fetchRnCompliance(rnId)
                .then((view) => {
                    if (!cancelled) {
                        setBanner(view.banner);
                    }
                })
                .catch(() => {
                    if (!cancelled) {
                        setBanner(null);
                    }
                });
            return () => {
                cancelled = true;
            };
        }, [rnId, show])
    );

    if (!banner || !show) {
        return null;
    }
    const tone = TONE[banner.tone];

    return (
        <TouchableOpacity
            activeOpacity={0.8}
            onPress={() => router.push("/compliance")}
            style={[styles.banner, { backgroundColor: tone.bg, borderColor: tone.border }]}
        >
            <MaterialCommunityIcons name="alert-decagram" size={22} color={tone.fg} />
            <View style={styles.text}>
                <Text style={[styles.title, { color: tone.fg }]}>{banner.title}</Text>
                <Text style={styles.message}>{banner.message}</Text>
            </View>
            <View style={[styles.action, { backgroundColor: tone.fg }]}>
                <Text style={styles.actionText}>{banner.buttonLabel}</Text>
            </View>
        </TouchableOpacity>
    );
}

const styles = StyleSheet.create({
    banner: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        borderRadius: radius.md,
        borderWidth: 1,
        paddingVertical: spacing.sm + 2,
        paddingHorizontal: spacing.md,
        marginTop: spacing.md
    },
    text: {
        flex: 1,
        gap: 1
    },
    title: {
        fontSize: 13,
        fontFamily: fonts.bold
    },
    message: {
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textSecondary
    },
    action: {
        borderRadius: radius.full,
        paddingHorizontal: spacing.md,
        paddingVertical: spacing.xs + 2
    },
    actionText: {
        fontSize: 12,
        fontFamily: fonts.bold,
        color: colors.surface
    }
});
